import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import type { RequestConfig } from "@app/config/axios/http";
import type {
    ApiStockKanbanContainerContentData,
    ApiStockKanbanContainerData,
    StockKanbanContainerFilter,
    StockKanbanMoveContainersResult,
} from "./types";

interface StockKanbanContainersResponse {
    data: ApiStockKanbanContainerData[];
}

interface StockKanbanContainerLotsResponse {
    data: ApiStockKanbanContainerContentData[];
}

/** Транспортный слой OMP — контейнеры КАНБАН. */
export const stockKanbanContainersApi = {
    /**
     * Загружает контейнеры КАНБАН по фильтру.
     * @param params фильтр контейнеров
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            filter?: StockKanbanContainerFilter;
        },
        config?: RequestConfig,
    ): Promise<ApiStockKanbanContainerData[]> => {
        const data = await ompApiClient.postOmega<StockKanbanContainersResponse>(
            "stock/stockKanbanContainer.loadByFilter",
            params,
            config,
        );
        return data.data;
    },

    /**
     * Берёт контейнер на комплектование (`ComplectState` 0 → 1). \
     * Проставляет `ComplectUser` и `ComplectDate`.
     * @param params код контейнера
     * @param config опции HTTP-запроса
     */
    takeForComplect: async (
        params: {
            code: number;
        },
        config?: RequestConfig,
    ): Promise<void> => {
        await ompApiClient.postOmega("stock/stockKanbanContainer.takeForComplect", params, config);
    },

    /**
     * Отменяет комплектование (`ComplectState` 1|2 → 0) и очищает партии.
     * @param params код контейнера
     * @param config опции HTTP-запроса
     */
    cancelComplect: async (
        params: {
            code: number;
        },
        config?: RequestConfig,
    ): Promise<void> => {
        await ompApiClient.postOmega("stock/stockKanbanContainer.cancelComplect", params, config);
    },

    /**
     * Завершает комплектование контейнера (`ComplectState` 1 → 2). \
     * Wire output пустой; ВЗН создаёт `moveContainers`.
     * @param params код контейнера
     * @param config опции HTTP-запроса
     */
    finishComplect: async (
        params: {
            code: number;
        },
        config?: RequestConfig,
    ): Promise<void> => {
        await ompApiClient.postOmega("stock/stockKanbanContainer.finishComplect", params, config);
    },

    /**
     * Перемещает скомплектованные контейнеры и создаёт ВЗН.
     * @param params коды контейнеров со статусом «комплектование завершено»
     * @param config опции HTTP-запроса
     */
    moveContainers: async (
        params: {
            codes: number[];
        },
        config?: RequestConfig,
    ): Promise<StockKanbanMoveContainersResult> => {
        return ompApiClient.postOmega<StockKanbanMoveContainersResult>(
            "stock/stockKanbanContainer.moveContainers",
            params,
            config,
        );
    },

    /**
     * Загружает партии, выбранные в контейнеры КАНБАН.
     * @param params коды контейнеров
     * @param config опции HTTP-запроса
     */
    getLots: async (
        params: {
            containerCodes: number[];
        },
        config?: RequestConfig,
    ): Promise<ApiStockKanbanContainerContentData[]> => {
        const data = await ompApiClient.postOmega<StockKanbanContainerLotsResponse>(
            "stock/stockKanbanContainer.getLots",
            params,
            config,
        );
        return data.data;
    },

    /**
     * Добавляет партию в контейнер КАНБАН.
     * @param params связь партия↔контейнер
     * @param config опции HTTP-запроса
     */
    addLot: async (
        params: {
            lot: ApiStockKanbanContainerContentData;
        },
        config?: RequestConfig,
    ): Promise<void> => {
        await ompApiClient.postOmega("stock/stockKanbanContainer.addLot", params, config);
    },

    /**
     * Удаляет связь партии с контейнером КАНБАН.
     * @param params код связи
     * @param config опции HTTP-запроса
     */
    removeLot: async (
        params: {
            code: number;
        },
        config?: RequestConfig,
    ): Promise<void> => {
        await ompApiClient.postOmega("stock/stockKanbanContainer.removeLot", params, config);
    },
} as const;
